import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { RandomUtils } from '@fytness/shared';
import { cn } from '@/lib/utils';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Icon } from '~/global/components/icon/Icon';
import { FullHeightWrapper } from '~/layout/components/full-height-wrapper/FullHeightWrapper';
import { PageHeader } from '~/layout/components/page-header/PageHeader';
import { SidebarLayoutHeader } from '~/layout/components/sidebar-layout-header/SidebarLayoutHeader';
import { StandardHeader } from '~/layout/components/standard-header/StandardHeader';
import { useModal } from '@/contexts/ModalContext';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { useHealth } from '~/health/contexts/health/Health';
import { LogWeightSheet } from '~/health/components/log-weight-sheet/LogWeightSheet';
import { useBodyMeasurementImport } from '~/health/hooks/useBodyMeasurementImport';
import { BodyTrendChart } from '~/health/components/body-trend-chart/BodyTrendChart';
import { MeasurementSourceBadge } from '~/health/components/measurement-source-badge/MeasurementSourceBadge';
import {
  BodyTrendSeries,
  type TrendMetric,
  type TrendPeriod,
} from '~/health/lib/body-measurement/body-trend-series';
import { BmiCalculator } from '~/health/lib/body-measurement/bmi-calculator';
import type { BodyMeasurementRecord } from '~/powersync/generated/schema';

const METRICS: TrendMetric[] = ['weight', 'bodyFat', 'muscle', 'bmi'];
const PERIODS: TrendPeriod[] = ['week', 'month', 'year'];

const METRIC_LABEL: Record<TrendMetric, string> = {
  weight: 'bodyComposition.weight',
  bodyFat: 'bodyComposition.bodyFat',
  muscle: 'bodyComposition.muscle',
  bmi: 'bodyComposition.bmi',
};
const PERIOD_LABEL: Record<TrendPeriod, string> = {
  week: 'bodyComposition.week',
  month: 'bodyComposition.month',
  year: 'bodyComposition.year',
};

export const BodyCompositionScreen = () => {
  const { t } = useTranslation('user');
  const { isAvailable, connect } = useHealth();
  const [metric, setMetric] = useState<TrendMetric>('weight');
  const [period, setPeriod] = useState<TrendPeriod>('month');

  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUser = userData?.currentUser;

  // Trend-open import; no foreground listener here (the Profile parent owns it).
  const { importLatest } = useBodyMeasurementImport({
    autoImportOnForeground: false,
  });

  const {
    data: measurements,
    isLoading,
    error,
  } = useLivePowerSyncQuery<BodyMeasurementRecord>(
    currentUser ? db.BodyMeasurement.queries.listByUser(currentUser.id) : null,
    [currentUser?.id],
  );

  const series = useMemo(
    () =>
      BodyTrendSeries.build(
        measurements ?? [],
        metric,
        period,
        currentUser?.height ?? null,
      ),
    [measurements, metric, period, currentUser?.height],
  );

  const latest = measurements?.[0]; // listByUser is newest-first
  const delta =
    series.current != null && series.previous != null
      ? Math.round((series.current - series.previous) * 10) / 10
      : null;

  // Manual weight entry — the path for non-health users (and anyone without a
  // scale). Writes a MANUAL BodyMeasurement, the same entity scale imports use.
  const powerSync = usePowerSync();
  const { showModal } = useModal();

  const logWeight = useCallback(
    async (weightKg: number) => {
      if (!currentUser) return;
      await db.BodyMeasurement.mutations.create(powerSync, {
        id: RandomUtils.uuid(),
        source: 'MANUAL',
        externalId: null,
        measuredAt: new Date().toISOString(),
        weight: weightKg,
        bodyFatPercent: null,
        leanMass: null,
        userId: currentUser.id,
      });
    },
    [currentUser, powerSync],
  );

  const openLogWeight = useCallback(() => {
    showModal(LogWeightSheet, {
      onSubmit: logWeight,
      defaultWeight: latest?.weight != null ? String(latest.weight) : undefined,
    });
  }, [showModal, logWeight, latest?.weight]);

  // Header actions (add-weight + sync), shared by the desktop and mobile headers.
  const actions = (
    <View className="flex-row items-center gap-1">
      {/* Manual add lives in the header once there's data; the empty state
          surfaces it as the primary CTA instead (so it's never duplicated). */}
      {measurements && measurements.length > 0 && (
        <Button
          variant="ghost"
          size="icon"
          onPress={openLogWeight}
          testID="body-composition-add-weight"
          accessibilityLabel={t('bodyComposition.addWeight')}
        >
          <Icon name="Plus" size={20} className="text-text-primary" />
        </Button>
      )}
      {isAvailable && (
        <Button
          variant="ghost"
          size="icon"
          onPress={() => importLatest()}
          testID="body-composition-sync"
          accessibilityLabel={t('bodyComposition.sync')}
        >
          <Icon name="RefreshCw" size={18} className="text-text-secondary" />
        </Button>
      )}
    </View>
  );

  // Same chrome as every other screen: the desktop sidebar header (subtitle +
  // title + avatar), and the compact PageHeader (with back button) on mobile.
  // SidebarLayoutHeader self-nulls on mobile; PageHeader hides on desktop.
  const header = (
    <>
      <SidebarLayoutHeader>
        <StandardHeader
          subtitle={t('bodyComposition.subtitle')}
          title={t('bodyComposition.title')}
        >
          {actions}
        </StandardHeader>
      </SidebarLayoutHeader>
      <PageHeader
        className="md:hidden"
        title={t('bodyComposition.title')}
        actions={actions}
      />
    </>
  );

  const renderBody = () => {
    if (isLoading && !measurements?.length) {
      return (
        <View className="flex-1 items-center justify-center p-10">
          <Icon name="LoaderCircle" size={28} className="text-text-muted" />
        </View>
      );
    }

    if (error) {
      return (
        <StateBlock
          icon="TriangleAlert"
          iconClass="text-negative"
          title={t('bodyComposition.errorTitle')}
          body={t('bodyComposition.errorBody')}
          action={{
            label: t('bodyComposition.retry'),
            onPress: () => importLatest(),
            testID: 'body-composition-retry',
          }}
        />
      );
    }

    if (!measurements?.length) {
      return (
        <StateBlock
          icon="Scale"
          iconClass="text-text-secondary"
          title={t('bodyComposition.emptyTitle')}
          body={t('bodyComposition.emptyBody')}
          action={{
            label: t('bodyComposition.addWeight'),
            onPress: openLogWeight,
            testID: 'body-composition-add-weight',
            primary: true,
          }}
          secondaryAction={
            isAvailable
              ? {
                  label: t('bodyComposition.connect'),
                  onPress: () => connect(),
                  testID: 'body-composition-connect',
                }
              : undefined
          }
          testID="body-composition-empty"
        />
      );
    }

    const unit = metricUnit(metric, t);
    const bmiMissing =
      metric === 'bmi' && series.current == null && latest?.weight != null;

    return (
      <ScrollView
        className="flex-1"
        contentContainerClassName="p-4 gap-6"
        showsVerticalScrollIndicator={false}
      >
        {/* Current value + delta + source */}
        <View className="gap-2">
          <Text className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            {t(METRIC_LABEL[metric])}
          </Text>
          <View className="flex-row items-center justify-between">
            <View className="flex-row items-baseline gap-1.5">
              <Text className="text-4xl font-bold">
                {series.current != null
                  ? String(series.current)
                  : t('settings.bodyMetrics.notAvailable')}
              </Text>
              {unit ? (
                <Text className="text-base font-medium text-text-secondary">
                  {unit}
                </Text>
              ) : null}
            </View>
            {delta != null && delta !== 0 && (
              <DeltaPill metric={metric} delta={delta} unit={unit} />
            )}
          </View>
          {latest?.source ? (
            <MeasurementSourceBadge source={latest.source} />
          ) : null}
        </View>

        {/* Metric switcher */}
        <View className="flex-row gap-1 rounded-xl bg-surface p-1">
          {METRICS.map((m) => {
            const active = m === metric;
            return (
              <Pressable
                key={m}
                onPress={() => setMetric(m)}
                testID={`metric-${m}`}
                className={cn(
                  'flex-1 items-center rounded-lg py-2',
                  active && 'bg-accent',
                )}
              >
                <Text
                  className={cn(
                    'text-xs font-medium',
                    active ? 'text-accent-text' : 'text-text-secondary',
                  )}
                >
                  {t(METRIC_LABEL[m])}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {/* Chart card */}
        <View className="gap-3 rounded-2xl bg-surface p-4">
          <View className="flex-row items-center justify-between">
            <Text className="text-sm font-semibold">
              {t(METRIC_LABEL[metric])}
            </Text>
            <View className="flex-row gap-0.5 rounded-full bg-surface-elevated p-0.5">
              {PERIODS.map((p) => {
                const active = p === period;
                return (
                  <Pressable
                    key={p}
                    onPress={() => setPeriod(p)}
                    testID={`period-${p}`}
                    className={cn(
                      'rounded-full px-2.5 py-1',
                      active && 'bg-accent',
                    )}
                  >
                    <Text
                      className={cn(
                        'text-xs font-medium',
                        active ? 'text-accent-text' : 'text-text-secondary',
                      )}
                    >
                      {t(PERIOD_LABEL[p])}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
          {series.points.length > 0 ? (
            <BodyTrendChart points={series.points} />
          ) : (
            <View className="h-40 items-center justify-center">
              <Text className="text-sm text-text-muted">
                {bmiMissing
                  ? t('bodyComposition.bmiHint')
                  : t('bodyComposition.emptyTitle')}
              </Text>
            </View>
          )}
        </View>

        {/* Latest composition chips */}
        {latest ? (
          <View className="gap-3">
            <Text className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              {t('settings.bodyMetrics.latestComposition')}
            </Text>
            <View className="flex-row gap-3">
              {(
                [
                  [
                    'bodyComposition.bodyFat',
                    latest.bodyFatPercent != null
                      ? `${latest.bodyFatPercent}%`
                      : null,
                  ],
                  [
                    'bodyComposition.muscle',
                    latest.leanMass != null
                      ? `${latest.leanMass} ${t('settings.bodyMetrics.weightUnit')}`
                      : null,
                  ],
                  [
                    'bodyComposition.bmi',
                    BmiCalculator.compute(latest.weight, currentUser?.height),
                  ],
                ] as const
              ).map(([labelKey, value]) => (
                <View
                  key={labelKey}
                  className="flex-1 gap-1 rounded-xl bg-surface p-3"
                >
                  <Text className="text-xs text-text-secondary">
                    {t(labelKey)}
                  </Text>
                  <Text className="text-lg font-semibold">
                    {value != null
                      ? String(value)
                      : t('settings.bodyMetrics.notAvailable')}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}
      </ScrollView>
    );
  };

  return (
    <FullHeightWrapper>
      {header}
      {renderBody()}
    </FullHeightWrapper>
  );
};

const metricUnit = (
  metric: TrendMetric,
  t: (key: string) => string,
): string => {
  switch (metric) {
    case 'weight':
    case 'muscle':
      return t('settings.bodyMetrics.weightUnit');
    case 'bodyFat':
      return '%';
    case 'bmi':
      return '';
  }
};

interface DeltaPillProps {
  metric: TrendMetric;
  delta: number;
  unit: string;
}

const DeltaPill = ({ metric, delta, unit }: DeltaPillProps) => {
  // Fat-loss-with-strength framing: down is good for weight/fat, up for muscle;
  // BMI stays neutral.
  const goodDirection =
    metric === 'muscle' ? 'up' : metric === 'bmi' ? 'neutral' : 'down';
  const isGood =
    goodDirection === 'neutral'
      ? null
      : goodDirection === 'up'
        ? delta > 0
        : delta < 0;
  const tone =
    isGood == null
      ? { bg: 'bg-surface-elevated', text: 'text-text-secondary', icon: '' }
      : isGood
        ? {
            bg: 'bg-positive-soft',
            text: 'text-positive',
            icon: 'text-positive',
          }
        : {
            bg: 'bg-negative-soft',
            text: 'text-negative',
            icon: 'text-negative',
          };

  return (
    <View
      className={cn(
        'flex-row items-center gap-1 rounded-full px-2 py-1',
        tone.bg,
      )}
    >
      <Icon
        name={delta < 0 ? 'TrendingDown' : 'TrendingUp'}
        size={14}
        className={tone.icon || 'text-text-secondary'}
      />
      <Text className={cn('text-xs font-semibold', tone.text)}>
        {`${Math.abs(delta)}${unit ? ` ${unit}` : ''}`}
      </Text>
    </View>
  );
};

interface StateAction {
  label: string;
  onPress: () => void;
  testID?: string;
  primary?: boolean;
}

interface StateBlockProps {
  icon: ComponentProps<typeof Icon>['name'];
  iconClass: string;
  title: string;
  body: string;
  action?: StateAction;
  secondaryAction?: StateAction;
  testID?: string;
}

const StateBlock = ({
  icon,
  iconClass,
  title,
  body,
  action,
  secondaryAction,
  testID,
}: StateBlockProps) => (
  <View
    testID={testID}
    collapsable={false}
    className="flex-1 items-center justify-center gap-3 p-8"
  >
    <View className="h-16 w-16 items-center justify-center rounded-full bg-surface">
      <Icon name={icon} size={28} className={iconClass} />
    </View>
    <Text className="text-center text-base font-semibold">{title}</Text>
    <Text className="text-center text-sm text-text-secondary">{body}</Text>
    {action ? (
      <Button
        variant={action.primary ? 'primary' : 'secondary'}
        size="sm"
        onPress={action.onPress}
        testID={action.testID}
        className="mt-1"
      >
        <Text>{action.label}</Text>
      </Button>
    ) : null}
    {secondaryAction ? (
      <Button
        variant="ghost"
        size="sm"
        onPress={secondaryAction.onPress}
        testID={secondaryAction.testID}
      >
        <Text className="text-text-secondary">{secondaryAction.label}</Text>
      </Button>
    ) : null}
  </View>
);
